import orderBy from "lodash/orderBy";
import last from "lodash/last";
import omit from "lodash/omit";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import cloneDeep from "lodash/cloneDeep";
import prettyBytes from "pretty-bytes";
import { formatUTCTime, orderByDate, TS_FORMAT } from "utils/date";
import { FORM_KEY } from "utils/constants/form";
import { omitAndSanitizeParams } from "components/Form/fields/helpers";
import { getShortLinkCheckoutScaffold, getRedirectUrls } from "../checkout";
import { TEMPLATE_CAMPAIGN_BUILDER_SECTIONS } from "../constants/template";
import {
  formattedPriceToUnitAmount,
  formatUnitAmount
} from "components/FormerEditor/common/currency";
import {
  CAMPAIGN_CHANNELS,
  CAMPAIGN_TYPE,
  PROSPECT_CUSTOMERS_MEAN_RANGE_LABEL_MAP
} from "utils/constants/campaign";
import { getUUID } from "../uuid";
import { PLATFORM_SOURCE } from "../constants/payment";
import differenceInDays from "date-fns/differenceInDays";
import QS from "qs";
import { SILENT } from "../constants/query";
import { NA_FALLBACK } from "../sale";
import { ON_OFF_SWITCH, statusToOnOff } from "../constants/table";
import format from "date-fns/format";

export const getCreateCampaignScaffold = ({
  uuid = getUUID(),
  application,
  merchantAccount
}) => {
  const redirectUrls = getRedirectUrls({
    application,
    merchantAccount
  });

  return {
    [FORM_KEY]: {},
    key: uuid,
    uuid,
    title: "",
    description: "",
    data: {
      application_id: application.id,
      merchant_account_id: merchantAccount.id,
      /**
       * Should there be a campaign type - one as default to start?
       */
      // type_uid: SHORT_LINK_ACTION_UID.MERCHANT_CHECKOUT,
      success_url: redirectUrls.successUrl,
      cancel_url: redirectUrls.cancelUrl,
      product: {
        checkout: getShortLinkCheckoutScaffold(),
        attachments: []
      },
      receiver: {
        audiences: [],
        datasources: []
      },
      send: {
        email: {
          service: "mailgun",
          schedule_time: ""
        }
      }
    }
  };
};

export const prepareEditCampaignInitialValues = ({ campaign }) => {
  const views = get(campaign, "data.views") || {};
  const currencies = Object.keys(views);
  const initialForm = {};
  if (currencies && currencies.length) {
    initialForm.currency = currencies[0];
  }

  return {
    [FORM_KEY]: initialForm,
    ...cloneDeep(campaign)
  };
};

export const prepareCreateCampaignsInitialValues = ({
  uuid,
  application,
  merchantAccount
}) => {
  return {
    campaigns: [
      getCreateCampaignScaffold({ uuid, application, merchantAccount })
    ]
  };
};

export const prepareCreateCampaignBuilderInitialValues = ({
  application,
  merchantAccount
}) => {
  return getCreateCampaignScaffold({
    application,
    merchantAccount,
    form: {
      activeSection: TEMPLATE_CAMPAIGN_BUILDER_SECTIONS.DETAILS
    }
  });
};

const prepareCampaignFromValues = (campaign) => {
  return campaign;
};

export const prepareCreateCampaigns = (campaigns) => {
  return omitAndSanitizeParams({
    campaigns: campaigns.map((values) => prepareCampaignFromValues(values))
  });
};

export const prepareCreateCampaign = (campaign) => {
  return omitAndSanitizeParams(prepareCampaignFromValues(campaign));
};

/**
 * Compare changes on keys for update
 * Note: db will merge the data field but that should be deprecated
 * @param {Object} initial form values
 * @param {Object} current form values
 */
export const prepareUpdateCampaign = (initial, current) => {
  // reduceKeyDifferences

  return omit(current, [FORM_KEY]);
};

export const campaignSizeLabel = (size) => prettyBytes(size);

export const prepareCampaignShow = ({ campaign }) => {
  const result = {
    id: campaign.id,
    key: campaign.uuid,
    uuid: campaign.uuid,
    title: campaign.title,
    subdomain: campaign.subdomain,
    description: campaign.description,
    createdAt: formatUTCTime(campaign.created_at)
  };

  return result;
};

export const prepareCampaignList = ({ campaigns, shouldOrder }) => {
  const ctxModels = shouldOrder
    ? orderBy(campaigns, ["created_at"], ["desc"])
    : campaigns;

  return ctxModels.map((campaign) => {
    return prepareCampaignShow({
      campaign
    });
  });
};

export const formatUploadedCampaign = (campaign, file) => {
  const updatedCampaign = {
    ...cloneDeep(campaign),
    data: file
  };

  return updatedCampaign;
};

/**
 * Check for the minimum presence of values for short link attribution like application and merchant account
 */
export const campaignFormLoading = ({
  loading,
  application,
  merchantAccount
}) => loading || isEmpty(application) || isEmpty(merchantAccount);

export const getCampaignSummary = ({ contacts }) => {
  return CAMPAIGN_CHANNELS.reduce((memo, channel) => {
    if (!memo[channel]) {
      memo[channel] = {
        summary: {},
        rows: {}
      };
    }
    contacts.forEach((contact) => {
      if (!memo[channel].summary[contact.currency]) {
        memo[channel].summary[contact.currency] = {
          total: 0
        };
      }
      if (!memo[channel].rows[contact.currency]) {
        memo[channel].rows[contact.currency] = [];
      }

      const unitAmount = formattedPriceToUnitAmount({
        formattedPrice: contact.amount,
        currency: contact.currency
      });
      memo[channel].summary[contact.currency].total += unitAmount;
      memo[channel].rows[contact.currency].push(contact);
    });

    Object.keys(memo[channel].summary).forEach((currency) => {
      const currencyConfig = memo[channel].summary[currency];
      const total = currencyConfig.total;

      currencyConfig.formattedTotal = formatUnitAmount({
        amount: total,
        currency
      });
      memo[channel].summary[currency] = currencyConfig;
    });

    return memo;
  }, {});
};

export const reduceCampaignDataSets = (data) => {
  const currencies = [];
  const sources = [];
  if (data && data.data) {
    for (const dataSource in data.data) {
      if (sources.indexOf(dataSource) === -1) {
        sources.push(dataSource);
      }

      for (const eventCurrencies in data.data[dataSource].events) {
        if (currencies.indexOf(eventCurrencies) === -1) {
          currencies.push(eventCurrencies);
        }
      }
    }
  }

  return {
    currencies,
    sources
  };
};

const prepareEventRow = ({
  event,
  application,
  shortLinkShortIdMap,
  today
}) => {
  const currency = event.currency;
  const source = event.platform_source;
  const id = event.event_id || event.campaign_object_id;

  const result = {
    id,
    source,
    amount_total: event.amount_total,
    customer_email: event.customer_email || NA_FALLBACK,
    customer: event.customer || NA_FALLBACK,
    // Dont provide a fallback if its not meant to be rendered when empty
    via: event.via,
    session_id: event.session_id,
    url: silentTakeUrl(event.url),
    status: event.status,
    customer_source: event.customer_source,
    source_uid: event.source_uid,
    source_uid_status: getSourceUIDStatus({
      sourceUID: event.source_uid,
      source,
      application,
      shortLinkShortIdMap
    })
  };
  if (currency) {
    result.currency = currency.toUpperCase();
    result.amount = formatUnitAmount({
      amount: event.amount_total || 0,
      currency
    });
  } else {
    console.error(`Event ${event.event_id} missing currency`);
  }

  extendWithTimestamps({ result, event, today });

  return result;
};

/**
 * Winback payload example
 * {
 *   "event_id": "sub_1KXqRPANVVVLTo16XoC6MIu1",
 *   "platform_source": "page",
 *   "amount_discount": 0,
 *   "references": {
 *       "prices": [
 *           "price_1Itzy4ANVVVLTo16iJPb046E"
 *       ],
 *       "coupons": [],
 *       "products": [
 *           "prod_ICiwDLPuU9BrhM"
 *       ],
 *       "taxRates": [],
 *       "discounts": [],
 *       "shippingRates": [],
 *       "promotionCodes": []
 *   },
 *   "created_at": "2022-02-27T17:21:50.000Z",
 *   "campaign_id": "a1cba649-4544-4e74-9d3c-14f33a34fe82",
 *   "url": "http://localhost:9000/v1/upsell/Fe26.2*upsell_checkout_session*45861c0845c61b8385ae8b560879844dfc2ac38def0a4ddff39beba95fb7d399*J-KTxyKgNBslhgIQJcq03A*Uu4eGKiVl5u1LLET1xSKUAq60gVH4sMKudChw1fHpg1jaUxM73_gSQ2jsTAoMBZ0VB7IIIZ9ZS38ZTKa8sKFyG29Lz_ct10SU58J5l4-5tfajhn9VlMIXmKXcoWy9YZR0md0LU5JQLYWrAiVQW5gCn-5519PxOHVQvOGjHn0h1kHnOS2kxb-sThSXq08SBelHuJjh7Q30DoCaP9Jh_fUGaf5PaVwxJsTtnvj0kt2k_K7lMI89_nX1_aDcquYuxeWxwQRSxYN8I55eXMPfPVx3bS5nWvu5Lt1kFWPALaa5HE__hv7Y8nRqow1838dHhxluoDs_pbAzLS_wQioprRl2wPINWg-hX3Jhpejg151PCYU8pP4qrpsXAXOG5UM-VeXMgmhO4Ugzg0M13WzSaznjA**21ee10f3dbd2412d32bf49bc9a58968cfd0c162b8a373b10ac14936f262ec293*upbp11jGW0BqshM4gBfMSx7B_tm1S8EcJG84KhJmUCs",
 *   "currency": "usd",
 *   "status": "upsellable",
 *   "service": "stripe",
 *   "ended_at": "2022-04-17T18:30:18.000Z",
 *   "customer_email": "shane@email.com",
 *   "amount_total": 6700,
 *   "timestamps": null,
 *   "customer": "cus_L67dgfvtrf4a1c",
 *   "object": "subscription"
 * }
 */
const getSourceUIDStatus = ({
  application,
  shortLinkShortIdMap,
  source,
  sourceUID
}) => {
  let status = ON_OFF_SWITCH.OFF;
  if (source && sourceUID) {
    if (source === PLATFORM_SOURCE.PAGE) {
      const routeStatus =
        application.routes[sourceUID] && application.routes[sourceUID].status;

      if (routeStatus) {
        status = statusToOnOff(routeStatus);
      }
    } else if (source === PLATFORM_SOURCE.LINK) {
      const linkStatus =
        shortLinkShortIdMap[sourceUID] && shortLinkShortIdMap[sourceUID].status;

      if (linkStatus) {
        status = statusToOnOff(linkStatus);
      }
    }
  }

  return status;
};

/**
 * Context:
 * - upsell: (created_at)
 * -- from when last target sale ocurred
 * - winback: (ended_at)
 * -- from when last subscription expired
 */
const extendWithTimestamps = ({ result, event, today }) => {
  const ctxTs = event.ended_at || event.created_at;
  let age = "";
  let daysBetweenTodayAndCtx = null;
  if (ctxTs) {
    const ctxDate = new Date(ctxTs);

    daysBetweenTodayAndCtx = Math.abs(differenceInDays(today, ctxDate));

    if (daysBetweenTodayAndCtx === 0) {
      age = `< 1 day`;
    } else if (daysBetweenTodayAndCtx === 1) {
      age = `1 day`;
    } else {
      age = `${daysBetweenTodayAndCtx} days`;
    }
  }
  result.age = age;
  result.days_between = daysBetweenTodayAndCtx;
  result.created_at = event.created_at
    ? new Date(event.created_at).toISOString()
    : null;
  result.ended_at = event.ended_at
    ? new Date(event.ended_at).toISOString()
    : null;

  return result;
};

const silentTakeUrl = (url) =>
  url
    ? `${url}${QS.stringify(
        {
          [SILENT]: true
        },
        { addQueryPrefix: true }
      )}`
    : null;

const prepareReportRows = ({
  campaign,
  application,
  shortLinkShortIdMap,
  today
}) => {
  const data = get(campaign, "data.data");
  const rows = [];
  const ctxToday = today || new Date();
  if (data) {
    for (const source in data) {
      const events = data[source] && data[source].events;
      if (events) {
        for (const currency in events) {
          const collection = events[currency];
          for (const eventId in collection) {
            const event = collection[eventId];
            rows.push(
              prepareEventRow({
                event,
                application,
                shortLinkShortIdMap,
                today: ctxToday
              })
            );
          }
        }
      }
    }
  }

  return rows;
};

/**
 * TODO: these functions should take a collection of keys which a single prepare function can then reduce
 */
const prepareRecoverSessionRows = prepareReportRows;
const prepareUpsellSessionRows = prepareReportRows;
const prepareParametersSessionRows = prepareReportRows;

const prepareProspectCustomersRows = ({ campaign, today }) => {
  const customerPayments = get(campaign, "data.data.customers.payments");
  // const totals = get(campaign, "data.data.totals");
  const rows = [];
  // const ctxToday = today || new Date();
  if (customerPayments) {
    for (const currency in customerPayments) {
      const currencyPayments = customerPayments[currency];

      for (const email in currencyPayments) {
        const customer = currencyPayments[email];

        const paymentDates = [];
        for (const paymentId in customer.payments) {
          paymentDates.push(customer.payments[paymentId].created);
        }
        const paymentCount = paymentDates.length;
        const lastDateUnixTs = last(orderBy(paymentDates));
        const lastDate = new Date(lastDateUnixTs * 1000);
        const meanRange = parseInt(customer.mean_range);
        const meanGrade = PROSPECT_CUSTOMERS_MEAN_RANGE_LABEL_MAP[meanRange];

        rows.push({
          id: email,
          currency: currency.toUpperCase(),
          ...[
            {
              key: "amount_total",
              name: "formatted_amount"
            },
            {
              key: "min",
              name: "formatted_min"
            },
            {
              key: "max",
              name: "formatted_max"
            },
            {
              key: "mean",
              name: "formatted_mean"
            }
          ].reduce((memo, { key, name }) => {
            const customerVal = customer[key];
            memo[name] = formatUnitAmount({
              amount: customerVal,
              currency
            });
            memo[key] = customerVal;
            return memo;
          }, {}),
          payments_count: paymentCount,
          mean_range: customer.mean_range,
          mean_grade: meanGrade,
          customer_email: email || NA_FALLBACK,
          customers: Object.keys(customer.customers),
          last_purchase_year: lastDate.getFullYear(),
          last_purchase_at: format(lastDate, TS_FORMAT.MDY)
          // Add the convenience url when ready
          // url: `${event.url}${QS.stringify(
          //   {
          //     [SILENT]: true
          //   },
          //   { addQueryPrefix: true }
          // )}`,
        });
      }
    }
  }
  return rows;
};

const prepareCollectPaymentRows = (data) => data;

const ROWS_FORMATTER = {
  [CAMPAIGN_TYPE.RECOVER_CHECKOUT_SESSION]: prepareRecoverSessionRows,
  [CAMPAIGN_TYPE.UPSELL_CHECKOUT_SESSION]: prepareUpsellSessionRows,
  [CAMPAIGN_TYPE.PARAMETERS]: prepareParametersSessionRows,
  [CAMPAIGN_TYPE.COLLECT_PAYMENT]: prepareCollectPaymentRows,
  [CAMPAIGN_TYPE.PROSPECT_CUSTOMERS]: prepareProspectCustomersRows
};

export const prepareCampaignRows = ({
  campaign,
  application,
  shortLinkShortIdMap,
  today,
  shouldOrder
}) => {
  const type = campaign.type;
  const ctxToday = today || new Date();

  const rows = ROWS_FORMATTER[type]
    ? ROWS_FORMATTER[type]({
        campaign,
        application,
        shortLinkShortIdMap,
        today: ctxToday
      })
    : [];

  return shouldOrder ? orderByDate(rows) : rows;
};

const reduceSummary = (rows, collection) => {
  const reducedTotals =
    Array.isArray(rows) && rows.length > 0
      ? rows.reduce((memo, { original }) => {
          const { currency } = original;
          if (currency) {
            if (!memo[currency]) {
              memo[currency] = collection.reduce((memo, { key }) => {
                memo[key] = 0;
                return memo;
              }, {});
            }
            collection.forEach(({ key }) => {
              memo[currency][key] += original[key] || 0;
            });
          }

          return memo;
        }, {})
      : null;
  const summaries = [];
  if (reducedTotals) {
    const currencies = Object.keys(reducedTotals);
    currencies.forEach((currency) => {
      collection.forEach(({ key, label }) => {
        summaries.push({
          label: `${label}${
            currencies.length > 1 ? ` ${currency.toUpperCase()}` : ""
          }`,
          value: formatUnitAmount({
            amount: reducedTotals[currency][key] || 0,
            currency
          })
        });
      });
    });
  }

  return summaries;
};

export const summarizeProspectCustomers = (summaryRows) => {
  return reduceSummary(summaryRows, [
    {
      key: "mean",
      label: "Mean value"
    }
  ]);
};

export const summarizeRecoveryCheckout = (summaryRows) => {
  return reduceSummary(summaryRows, [
    {
      key: "amount_total",
      label: "Amount total"
    }
  ]);
};

export const summarizeUpsellCheckout = (summaryRows) => {
  return reduceSummary(summaryRows, [
    {
      key: "amount_total",
      label: "Amount total"
    }
  ]);
};
